import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"

import de from "./de.json"
import en from "./en.json"
import es from "./es.json"
import fr from "./fr.json"
import ja from "./ja.json"
import ko from "./ko.json"
import pt from "./pt.json"
import ru from "./ru.json"
import zhHant from "./zh-Hant.json"
import zhHans from "./zh.json"
import { queryClient } from "../util/queryClient"
import { localStorageItems } from "../util/storage"

export const SUPPORTED_LANGUAGES = ["de", "en", "es", "fr", "pt", "ja", "ko", "ru", "zh-Hans", "zh-Hant"] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

/** Endonyms — a language picker should read in the language it selects. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
  es: "Español",
  fr: "Français",
  pt: "Português",
  ja: "日本語",
  ko: "한국어",
  ru: "Русский",
  "zh-Hans": "简体中文",
  "zh-Hant": "繁體中文",
}

export const resources = {
  de: { translation: de },
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  ja: { translation: ja },
  ko: { translation: ko },
  pt: { translation: pt },
  ru: { translation: ru },
  "zh-Hans": { translation: zhHans },
  "zh-Hant": { translation: zhHant },
} as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: "en",
    // NB: do NOT enable nonExplicitSupportedLngs. With script-tagged codes in
    // supportedLngs (zh-Hans/zh-Hant), it strips them to their language part
    // ("zh") for the supported-code check — and since bare "zh" isn't listed,
    // zh-Hans/zh-Hant get rejected and fall back to English. Region variants
    // (es-MX, fr-CA) still normalize to their base via convertDetectedLanguage
    // below plus i18next's default resolve hierarchy, so we don't need it.
    // Keys are a flat dictionary (e.g. WELCOME_TERMS), not nested — so don't
    // treat "." or ":" in a key as a path/namespace separator.
    keySeparator: false,
    nsSeparator: false,
    detection: {
      // Mirrors our original priority chain: ?lang → localStorage → browser.
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      // Persist the resolved language so returning visitors keep it without
      // needing ?lang again. Cached under the same key the API client reads.
      caches: ["localStorage"],
      lookupLocalStorage: localStorageItems.locale,
      // Chinese needs script-level resolution (Simplified vs Traditional), which
      // the old load:"languageOnly" couldn't express — it collapsed zh-TW and
      // zh-CN to the same "zh". So we normalize every detected tag here instead:
      // zh-Hant / zh-TW / zh-HK / zh-MO → zh-Hant, other zh-* → zh-Hans, and all
      // non-Chinese locales fall back to their base (es-MX → es, fr-CA → fr),
      // preserving the region-normalizing behavior we previously got from languageOnly.
      convertDetectedLanguage: (lng: string) => {
        const lower = lng.toLowerCase()
        if (lower.startsWith("zh")) {
          return /hant|tw|hk|mo/.test(lower) ? "zh-Hant" : "zh-Hans"
        }
        return lng.split("-")[0]
      },
    },
    interpolation: {
      // React already escapes values, so i18next doesn't need to.
      escapeValue: false,
    },
  })

/**
 * Switch the app's language and re-fetch everything that depends on it.
 *
 * The detector's localStorage cache persists the choice on `languageChanged`, so a
 * returning visitor keeps it. `joon-api` builds a fresh client per request and reads
 * `i18n.language` for the `Accept-Language` header, so by the time the queries below
 * re-run they already ask the backend for the new language. Server-owned copy (shop
 * items, coaching suggestions, chat summaries) is translated per request, so every
 * cached response is stale the moment the language changes.
 */
export const changeLanguage = async (language: Language) => {
  if (language === i18n.resolvedLanguage) return
  await i18n.changeLanguage(language)
  await queryClient.invalidateQueries()
}

export default i18n
